import React, { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import * as yup from "yup";
import { TextFormField } from "../../components/textform/TextFormField.tsx";
import { useNavigate } from "react-router-dom";
import {
  CreateLocation,
  GenerateLocationPlaylist,
} from "../../services/locations.js";
import { MapPin, Video, MinusSquare } from "react-feather";
import AddVideoDialog from "../../components/modal/videodialog.js";
import PopSnackbar from "../../components/snackbar/index.js";
import {
  AddVideoToLocation,
  GetVideosByCategory,
  UploadVideo,
} from "../../services/video.js";
import MultiSelectFormField from "../../components/multiselectform/MultiSelectFormField.tsx";
import { GetAllDevices } from "../../services/devices.js";
import { GetTags } from "../../services/tag.js";

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
  },
};

export const validation = yup.object().shape({
  location_name: yup.string().required("Location name is required"),
});

export default function AddLocation() {
  const [toastStatus, setToastStatus] = useState(false);
  const [toastDescription, setToastDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const navigate = useNavigate();
  const [deviceData, setDeviceData] = useState([]);
  const [tagData, setTagData] = useState([]);

  let partnerId = new URLSearchParams(window.location.search).get("partner");
  if (partnerId == null) {
    partnerId = "";
  }

  async function GetData() {
    try {
      setLoadingLocations(true);

      const responseDevices = await GetAllDevices();
      if (responseDevices.status !== "OK") {
        throw new Error(
          responseDevices.error ||
            "Something went wrong, please try again later."
        );
      }

      setDeviceData(responseDevices.data);

      const responseTags = await GetTags();
      if (responseTags.status !== "OK") {
        throw new Error(
          responseTags.error || "Something went wrong, please try again later."
        );
      }

      setTagData(responseTags.data);
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoadingLocations(false);
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSubmitVideo = (videoArray) => {
    setSelectedVideos((prev) => [...prev, ...videoArray]);
  };

  const handleSelectExistingVideo = (videoArray) => {
    setSelectedVideos((prev) => [...prev, ...videoArray]);
  };

  const handleRemoveVideo = (index) => {
    setSelectedVideos((prev) => prev.filter((_, i) => i !== index));
  };

  const GetInitialVideos = async () => {
    try {
      const response = await GetVideosByCategory(0);
      if (response.status !== "OK") {
        throw new Error(
          response.error || "Something went wrong, please try again later."
        );
      }

      const selectedAllPartnersVideos = response.data.filter(
        (video) => video.selected_all_partners
      );

      const selectedAllLocationsVideos = response.data.filter(
        (video) =>
          video.selected_all_locations &&
          video.locations.some(
            (location) => location.partner_id.toString() === partnerId
          )
      );

      const combinedVideos = [
        ...selectedAllPartnersVideos,
        ...selectedAllLocationsVideos,
      ];

      const uniqueCombinedVideos = combinedVideos.filter(
        (video, index, self) =>
          index === self.findIndex((v) => v.id === video.id)
      );

      setSelectedVideos(uniqueCombinedVideos);
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    }
  };

  const handleStatus = (description, setStatus, setDescription) => {
    setStatus(true);
    setDescription(description);
    setTimeout(() => {
      setStatus(false);
      setDescription("");
    }, 5000);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const devices = values.devices.map((deviceId, index) => {
        const device = deviceData.find((d) => d.device_id === deviceId);
        const tags = values.tags?.[index]
          ?.map((tagId) => {
            const tag = tagData.find((t) => t.ID === tagId);
            return tag ? { ID: tag.ID, name: tag.name } : null;
          })
          .filter(Boolean);

        return {
          device_id: device?.device_id,
          name: device?.name,
          tags: tags || [],
        };
      });

      const data = {
        name: values.location_name,
        county: values.county || "",
        city: values.city || "",
        address: values.address || "",
        observations: values.observations || "",
        contact1: values.contact1 || "",
        contact2: values.contact2 || "",
        contact3: values.contact3 || "",
        contact4: values.contact4 || "",
        contact5: values.contact5 || "",
        devices,
      };

      const locationResponse = await CreateLocation(
        JSON.stringify(data),
        partnerId
      );
      if (locationResponse.status !== "OK") {
        throw new Error(locationResponse.error || "Failed to create location.");
      }

      const createdLocation = locationResponse.data;

      for (const video of selectedVideos) {
        if (video.id || video.ID) {
          const linkResponse = await AddVideoToLocation(
            video.id || video.ID,
            createdLocation.ID
          );
          if (linkResponse.status !== "OK") {
            throw new Error(
              linkResponse.error || "Failed to link video to location."
            );
          }
        } else if (video.file) {
          const videoData = new FormData();
          videoData.append("file", video.file);
          videoData.append("title", video.title);
          videoData.append("duration", video.duration);
          videoData.append("is_landscape", video.is_landscape);
          videoData.append("categories", JSON.stringify([2]));
          videoData.append(
            "location_ids",
            JSON.stringify([createdLocation.ID])
          );

          const uploadResponse = await UploadVideo(videoData);
          if (uploadResponse.status !== "OK") {
            throw new Error(
              uploadResponse.error || "Failed to upload video for location."
            );
          }
        }
      }

      const generateResponse = await GenerateLocationPlaylist(
        createdLocation.ID
      );
      if (
        generateResponse.status === "OK" &&
        generateResponse.regenerationTriggered
      ) {
        handleStatus(
          "Playlist regeneration triggered.",
          setToastStatus,
          setToastDescription
        );
      }

      handleStatus(
        "The location has been successfully created.",
        setToastStatus,
        setToastDescription
      );
      navigate(-1);
    } catch (error) {
      handleStatus(error.message, setToastStatus, setToastDescription);
    } finally {
      setLoading(false);
    }
  };

  const FormSection = ({ icon, title, children }) => (
    <div
      style={{
        marginBottom: "20px",
        padding: "20px",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        backgroundColor: "white",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          columnGap: "10px",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>{icon}</div>
        <div
          style={{
            fontSize: "1.75rem",
            fontFamily: "SeagoeUIBold",
          }}
        >
          {title}
        </div>
      </div>
      <div
        className="form-section-content"
        style={{ display: "flex", flexDirection: "column" }}
      >
        {children}
      </div>
    </div>
  );

  useEffect(() => {
    if (!loadingLocations) {
      GetData();
      GetInitialVideos();
    }
  }, []);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "20px",
        paddingBottom: "100px",
      }}
    >
      {toastStatus && <PopSnackbar message={toastDescription} />}
      <div
        className="form-container"
        style={{ width: "100%", opacity: loading ? 0.5 : 1 }}
      >
        <Formik
          validationSchema={validation}
          initialValues={{
            location_name: "",
            county: "",
            city: "",
            address: "",
            observations: "",
            contact1: "",
            contact2: "",
            contact3: "",
            contact4: "",
            contact5: "",
            devices: [], // Directly link devices to the location
          }}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({ setFieldValue, setFieldTouched, values }) => (
            <Form
              id="location-form"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  gap: "20px",
                }}
              >
                <div
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <FormSection
                    title="Detalii generale"
                    icon={<MapPin size={"1.5rem"} />}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                      }}
                    >
                      <>
                        <Field
                          name="location_name"
                          label="Nume locatie"
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="e.g., Regina Maria"
                        />
                        <Field
                          name="county"
                          label="Judet"
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="e.g., Bucuresti"
                        />
                        <Field
                          name="city"
                          label="Oras"
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="e.g., Sector 1"
                        />
                        <Field
                          name="address"
                          label="Adresa"
                          component={TextFormField}
                          sx={styles.select}
                          style={{ width: "100%" }}
                          placeholder="Introdu adresa locatiei"
                        />
                      </>
                    </div>
                  </FormSection>

                  <FormSection
                    title="Aloca playere"
                    icon={<MapPin size={"1.5rem"} />}
                  >
                    {/* Select All button */}
                    <div style={{ marginBottom: "10px", textAlign: "right" }}>
                      <button
                        type="button"
                        style={{
                          background: "none",
                          border: "1px solid #ccc",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const allDeviceIds = deviceData
                            .filter(
                              (device) =>
                                device.location_id === null &&
                                device.name !== null
                            )
                            .map((device) => device.device_id);

                          const areAllSelected = allDeviceIds.every((id) =>
                            values.devices.includes(id)
                          );

                          if (areAllSelected) {
                            setFieldValue("devices", []); // deselect all
                          } else {
                            setFieldValue("devices", allDeviceIds); // select all
                          }
                        }}
                      >
                        {values.devices.length ===
                        deviceData.filter(
                          (d) => d.location_id === null && d.name !== null
                        ).length
                          ? "Deselecteaza toate"
                          : "Selecteaza toate"}
                      </button>
                    </div>

                    {/* Main devices field */}
                    <Field
                      name="devices"
                      label="Selecteaza playere"
                      component={MultiSelectFormField}
                      options={deviceData
                        .filter(
                          (device) =>
                            device.location_id === null && device.name !== null
                        )
                        .map((device) => ({
                          value: device.device_id,
                          label: device.name,
                        }))}
                      style={{ width: "100%" }}
                      sx={styles.select}
                    />

                    {/* Device tags */}
                    {values.devices.map((deviceId, index) => {
                      const device = deviceData.find(
                        (d) => d.device_id === deviceId
                      );

                      return (
                        <div
                          key={deviceId}
                          style={{
                            marginTop: "20px",
                            padding: "10px",
                            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                            borderRadius: "8px",
                            backgroundColor: "white",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "1.25rem",
                              marginBottom: "10px",
                            }}
                          >
                            {device.name}
                          </div>
                          <Field
                            name={`tags[${index}]`}
                            label="Selecteaza taguri pentru player"
                            component={MultiSelectFormField}
                            options={tagData.map((tag) => ({
                              value: tag.ID,
                              label: tag.name,
                            }))}
                            style={{ width: "100%" }}
                            sx={styles.select}
                          />
                        </div>
                      );
                    })}
                  </FormSection>

                  <FormSection
                    title="Configurare spoturi"
                    icon={<Video size={"1.5rem"} />}
                  >
                    {selectedVideos.map((video, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: "10px",
                          padding: "10px",
                          backgroundColor: "#f9f9f9",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "1rem",
                          fontFamily: "SeagoeUI",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <span style={{ fontFamily: "SeagoeUIBold" }}>
                            {video.title}
                          </span>
                          <span style={{ fontFamily: "SeagoeUI" }}>.mp4</span>
                        </span>
                        <MinusSquare
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.transform = "scale(1.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.transform = "scale(1)")
                          }
                          onClick={() => handleRemoveVideo(index)}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="add-video-button"
                      disabled={loading}
                      onClick={() => setIsModalOpen(true)}
                    >
                      Adauga spoturi
                    </button>
                    <AddVideoDialog
                      open={isModalOpen}
                      onClose={handleModalClose}
                      onSubmit={handleSubmitVideo}
                      onSelect={handleSelectExistingVideo}
                      instantUpload={false}
                      canAddVideo={false}
                    />
                  </FormSection>
                </div>

                {/* Contact Section */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <FormSection
                    title="Detalii contact"
                    icon={<MapPin size={"1.5rem"} />}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                      }}
                    >
                      <Field
                        name="observations"
                        label="Observatii"
                        component={TextFormField}
                        sx={styles.select}
                        style={{ width: "100%" }}
                        placeholder="Observatii despre locatie"
                        multiline
                        rows={3}
                      />
                      <Field
                        name="contact1"
                        label="Contact 1"
                        component={TextFormField}
                        sx={styles.select}
                        style={{ width: "100%" }}
                        placeholder="Contact 1"
                      />
                      <Field
                        name="contact2"
                        label="Contact 2"
                        component={TextFormField}
                        sx={styles.select}
                        style={{ width: "100%" }}
                        placeholder="Contact 2"
                      />
                      <Field
                        name="contact3"
                        label="Contact 3"
                        component={TextFormField}
                        sx={styles.select}
                        style={{ width: "100%" }}
                        placeholder="Contact 3"
                      />
                      <Field
                        name="contact4"
                        label="Contact 4"
                        component={TextFormField}
                        sx={styles.select}
                        style={{ width: "100%" }}
                        placeholder="Contact 4"
                      />
                      <Field
                        name="contact5"
                        label="Contact 5"
                        component={TextFormField}
                        sx={styles.select}
                        style={{ width: "100%" }}
                        placeholder="Contact 5"
                      />
                    </div>
                  </FormSection>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: "30px",
          borderTop: "1px solid rgba(0, 0, 0, 0.06)",
          zIndex: 1000,
          display: "flex",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f9fa",
            color: "#495057",
            padding: "14px 32px",
            border: "2px solid #e9ecef",
            borderRadius: "8px",
            fontFamily: "SeagoeUIBold",
            cursor: "pointer",
            fontSize: "1em",
            minWidth: "180px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#e9ecef";
            e.target.style.borderColor = "#dee2e6";
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#f8f9fa";
            e.target.style.borderColor = "#e9ecef";
            e.target.style.transform = "translateY(0px)";
            e.target.style.boxShadow = "none";
          }}
        >
          Anuleaza
        </button>
        <button
          disabled={loading}
          form="location-form"
          type="submit"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: loading ? "#ccc" : "#000",
            color: "white",
            fontFamily: "SeagoeUIBold",
            padding: "14px 32px",
            border: "none",
            borderRadius: "8px",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "1em",
            minWidth: "200px",
            opacity: loading ? 0.7 : 1,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow: loading ? "none" : "0 4px 14px rgba(0, 0, 0, 0.15)",
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = "#1e3a8a";
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 8px 25px rgba(30, 58, 138, 0.3)";
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.backgroundColor = "#000";
              e.target.style.transform = "translateY(0px)";
              e.target.style.boxShadow = "0 4px 14px rgba(0, 0, 0, 0.15)";
            }
          }}
        >
          {loading ? "Se adauga..." : "Adauga locatie"}
        </button>
      </div>
    </div>
  );
}
